import * as Plot from "@observablehq/plot";
import { useCallback, useEffect, useMemo, useState } from "react";

import Graphique from "./Graphique";
import { ApercuTypst } from "./Maths";
import { api, type Analyse, type EtatAnalyse } from "./api";
import { couleurExercice, neutres, sequentielle } from "./palette";
import type { Resume } from "./api";

/**
 * Marques d'une boîte à moustaches horizontale.
 *
 * Observable Plot fournit `boxX`, mais avec la convention de l'écart
 * interquartile : les moustaches s'arrêtent à 1,5 × IQR et le reste devient
 * des points atypiques. Ici les moustaches vont au minimum et au maximum —
 * ce sont les cinq nombres du résumé qui sont demandés, pas une détection de
 * valeurs aberrantes.
 */
function marquesBoite(
  resume: Resume,
  categorie: string,
  couleur: string,
): Plot.Markish[] {
  const donnee = [{ ...resume, categorie }];
  return [
    Plot.ruleY(donnee, {
      y: "categorie", x1: "minimum", x2: "maximum", stroke: couleur,
      strokeWidth: 1.2,
    }),
    Plot.barX(donnee, {
      y: "categorie", x1: "q1", x2: "q3",
      fill: couleur, fillOpacity: 0.28, stroke: couleur, strokeWidth: 1.2,
      insetTop: 6, insetBottom: 6,
    }),
    Plot.tickX(donnee, {
      y: "categorie", x: "mediane", stroke: neutres.texte, strokeWidth: 2,
      inset: 6,
    }),
    Plot.dot(donnee, {
      y: "categorie", x: "moyenne", symbol: "diamond", r: 4,
      fill: neutres.surface, stroke: neutres.texte, strokeWidth: 1.2,
    }),
  ];
}

const NOMBRE = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });

function Indicateur({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div className="indicateur">
      <span className="libelle">{libelle}</span>
      <span className="valeur">{valeur}</span>
    </div>
  );
}

export default function Statistiques({ dsId }: { dsId: number }) {
  const [etat, setEtat] = useState<EtatAnalyse | null>(null);
  const [brouillon, setBrouillon] = useState<Analyse | null>(null);
  const [travail, setTravail] = useState<"repos" | "redaction" | "production">("repos");
  const [message, setMessage] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<number | null>(null);

  const recharger = useCallback(() => {
    api.lireAnalyse(dsId).then((lu) => {
      setEtat(lu);
      setBrouillon(lu.analyse);
    });
    api.lireDocuments(dsId).then((documents) => {
      const analyse = documents.find((d) => d.type === "stats");
      setDocumentId(analyse ? analyse.id : null);
    });
  }, [dsId]);

  useEffect(recharger, [recharger]);

  const indicateurs = etat?.indicateurs;

  // Classes d'un point, comme l'histogramme du document PDF : les deux
  // représentations doivent montrer la même chose.
  const distribution = useMemo(() => {
    if (!indicateurs) return [];
    const classes = new Map<number, number>();
    for (const note of indicateurs.notes) {
      const classe = Math.round(note);
      classes.set(classe, (classes.get(classe) ?? 0) + 1);
    }
    return [...classes.entries()]
      .map(([note, effectif]) => ({ note, effectif }))
      .sort((a, b) => a.note - b.note);
  }, [indicateurs]);

  // Une question que personne n'a encore corrigée n'a pas de taux : la barrer
  // à 0 % la ferait passer pour la plus ratée du devoir. On l'écarte du
  // graphique et on dit combien manquent.
  const donneesQuestions = useMemo(() => {
    if (!etat || !indicateurs) return [];
    return etat.questions
      .filter((question) => (indicateurs.corrigees_par_question[question.ref] ?? 0) > 0)
      .map((question) => ({
        ref: question.ref + (question.bonus ? " ★" : ""),
        taux: (indicateurs.reussite_par_question[question.ref] ?? 0) * 100,
        exercice: question.exercice,
      }));
  }, [etat, indicateurs]);

  const questionsEnAttente = (etat?.questions.length ?? 0) - donneesQuestions.length;

  // Le plus petit effectif parmi les questions corrigées : tant qu'il est
  // sous l'effectif présent, les taux portent sur une partie de la classe.
  const socleMinimal = useMemo(() => {
    if (!etat || !indicateurs) return 0;
    const effectifs = etat.questions
      .map((q) => indicateurs.corrigees_par_question[q.ref] ?? 0)
      .filter((n) => n > 0);
    return effectifs.length ? Math.min(...effectifs) : 0;
  }, [etat, indicateurs]);

  const donneesCompetences = useMemo(() => {
    if (!etat || !indicateurs) return [];
    return etat.competences.map((competence) => ({
      code: competence.code,
      libelle: competence.libelle,
      taux: (indicateurs.reussite_par_competence[competence.code] ?? 0) * 100,
    }));
  }, [etat, indicateurs]);

  const rediger = async () => {
    setTravail("redaction");
    setMessage(null);
    try {
      const resultat = await api.redigerAnalyse(dsId);
      setMessage(
        `Analyse rédigée (${resultat.cout_dollars?.toFixed(3)} $).` +
          (resultat.anomalies.length
            ? ` ${resultat.anomalies.length} remarque(s) : ${resultat.anomalies[0].message}`
            : ""),
      );
      recharger();
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setTravail("repos");
    }
  };

  const enregistrer = async () => {
    if (!brouillon) return;
    try {
      await api.modifierAnalyse(dsId, brouillon);
      setMessage("Analyse enregistrée.");
      recharger();
    } catch (e) {
      setMessage((e as Error).message);
    }
  };

  const produire = async () => {
    setTravail("production");
    try {
      const document = await api.produireAnalyse(dsId);
      setDocumentId(document.id);
      setMessage(`${Math.round(document.octets / 1024)} ko produits.`);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setTravail("repos");
    }
  };

  if (!etat || !indicateurs) return <p>Chargement…</p>;

  if (indicateurs.effectif === 0 || indicateurs.notes.length === 0) {
    return (
      <p className="aide">
        Aucune note saisie : les statistiques apparaîtront dès les premières
        corrections.
      </p>
    );
  }

  const modifier = (transformer: (a: Analyse) => Analyse) =>
    setBrouillon((precedent) => (precedent ? transformer(precedent) : precedent));

  return (
    <section className="statistiques">
      <header className="saisie-entete">
        <div>
          <h2>Analyse — {etat.ds.titre}</h2>
          <p className="sous-titre">
            {indicateurs.effectif} copies
            {indicateurs.absents.length > 0 &&
              ` · ${indicateurs.absents.length} absents`}
            {etat.retouchee && " · analyse retouchée"}
          </p>
        </div>
        <div className="actions">
          <button type="button" onClick={rediger} disabled={travail !== "repos"}>
            {travail === "redaction"
              ? "Rédaction en cours…"
              : etat.analyse
                ? "Réécrire l'analyse (payant)"
                : "Rédiger l'analyse (payant)"}
          </button>
          <button
            type="button"
            onClick={produire}
            disabled={travail !== "repos" || !etat.analyse}
            title={etat.analyse ? undefined : "Rédiger l'analyse d'abord"}
          >
            {travail === "production" ? "Composition…" : "Produire le document"}
          </button>
          {documentId !== null && (
            <a className="telechargement" href={api.lienTelechargement(documentId)}>
              Télécharger le PDF
            </a>
          )}
        </div>
      </header>

      {message && <p className="message">{message}</p>}

      <div className="indicateurs">
        <Indicateur libelle="Moyenne" valeur={NOMBRE.format(indicateurs.moyenne)} />
        <Indicateur libelle="Médiane" valeur={NOMBRE.format(indicateurs.mediane)} />
        <Indicateur libelle="Écart-type" valeur={NOMBRE.format(indicateurs.ecart_type)} />
        <Indicateur libelle="Minimum" valeur={NOMBRE.format(indicateurs.minimum)} />
        <Indicateur libelle="Maximum" valeur={NOMBRE.format(indicateurs.maximum)} />
      </div>
      <p className="aide">
        Un quart des élèves sous {NOMBRE.format(indicateurs.quartiles[0])}, la
        moitié sous {NOMBRE.format(indicateurs.quartiles[1])}, trois quarts sous{" "}
        {NOMBRE.format(indicateurs.quartiles[2])}. Barème sur{" "}
        {NOMBRE.format(etat.ds.total_points)} points.
      </p>

      <h3>Résumé en cinq nombres</h3>
      <Graphique
        options={{
          height: 92,
          marginLeft: 42,
          marginTop: 6,
          x: { label: "note sur 20", domain: [0, etat.ds.total_points], grid: true },
          y: { label: null, axis: null },
          style: { fontSize: "11px" },
          marks: [
            ...marquesBoite(indicateurs.resume, "classe", sequentielle.hue),
            Plot.ruleX([indicateurs.moyenne], {
              stroke: neutres.texte_secondaire, strokeDasharray: "3 2",
              strokeWidth: 0.8,
            }),
          ],
        }}
      />
      <p className="aide">
        Les moustaches vont du minimum au maximum, la boîte des quartiles Q1 à
        Q3, le trait épais marque la médiane et le losange la moyenne.
      </p>

      {Object.keys(indicateurs.resume_par_exercice).length > 0 && (
        <>
          <h3>Dispersion par exercice</h3>
          <p className="aide">
            Chaque exercice ramené à son propre barème : sans cela, l'exercice le
            plus lourd paraîtrait mieux réussi que les autres.
          </p>
          <Graphique
            options={{
              height: 42 * Object.keys(indicateurs.resume_par_exercice).length + 46,
              marginLeft: 46,
              x: {
                label: "réussite", domain: [0, 1], grid: true,
                tickFormat: (t: number) => `${Math.round(t * 100)} %`,
              },
              y: {
                label: null,
                domain: Object.keys(indicateurs.resume_par_exercice)
                  .sort((a, b) => Number(a) - Number(b))
                  .map((n) => `Ex ${n}`),
              },
              style: { fontSize: "11px" },
              marks: Object.entries(indicateurs.resume_par_exercice)
                .sort(([a], [b]) => Number(a) - Number(b))
                .flatMap(([numero, resume]) =>
                  marquesBoite(resume, `Ex ${numero}`, couleurExercice(Number(numero))),
                ),
            }}
          />
        </>
      )}

      <h3>Distribution des notes</h3>
      <Graphique
        options={{
          height: 200,
          marginLeft: 42,
          x: { label: "note sur 20", domain: [0, 21] },
          y: { label: "élèves", grid: true },
          style: { fontSize: "11px" },
          marks: [
            Plot.rectY(distribution, {
              x1: (d: { note: number }) => d.note - 0.5,
              x2: (d: { note: number }) => d.note + 0.5,
              y: "effectif",
              fill: sequentielle.hue,
              insetLeft: 0.5,
              insetRight: 0.5,
            }),
            Plot.ruleX([indicateurs.moyenne], {
              stroke: neutres.texte_secondaire, strokeDasharray: "4 2",
            }),
            Plot.ruleY([0], { stroke: neutres.axe }),
          ],
        }}
      />

      <h3>Réussite par question</h3>
      {(questionsEnAttente > 0 || (socleMinimal > 0 && socleMinimal < indicateurs.effectif)) && (
        <p className="aide">
          Taux calculés sur les copies corrigées, non sur la classe entière.
          {socleMinimal > 0 && socleMinimal < indicateurs.effectif &&
            ` La question la moins avancée en compte ${socleMinimal} sur ${indicateurs.effectif}.`}
          {questionsEnAttente > 0 &&
            ` ${questionsEnAttente} question(s) pas encore corrigée(s), absente(s) du graphique.`}
        </p>
      )}
      <Graphique
        options={{
          height: 24 * donneesQuestions.length + 40,
          marginLeft: 78,
          x: { label: "réussite", domain: [0, 100], grid: true, tickFormat: (t: number) => `${t} %` },
          y: { label: null, domain: donneesQuestions.map((q) => q.ref) },
          style: { fontSize: "11px" },
          marks: [
            Plot.barX(donneesQuestions, {
              x: "taux", y: "ref",
              fill: (d: { exercice: number }) => couleurExercice(d.exercice),
              sort: null,
            }),
            Plot.text(donneesQuestions, {
              x: "taux", y: "ref", text: (d: { taux: number }) => `${Math.round(d.taux)} %`,
              textAnchor: "start", dx: 4, fill: neutres.texte_secondaire,
            }),
            Plot.ruleX([0], { stroke: neutres.axe }),
          ],
        }}
      />

      {donneesCompetences.length > 0 && (
        <>
          <h3>Réussite par compétence</h3>
          <Graphique
            options={{
              height: 26 * donneesCompetences.length + 40,
              marginLeft: 300,
              x: { label: "réussite", domain: [0, 100], grid: true, tickFormat: (t: number) => `${t} %` },
              y: { label: null, domain: donneesCompetences.map((c) => c.libelle) },
              style: { fontSize: "11px" },
              marks: [
                Plot.barX(donneesCompetences, {
                  x: "taux", y: "libelle", fill: sequentielle.hue, sort: null,
                }),
                Plot.text(donneesCompetences, {
                  x: "taux", y: "libelle",
                  text: (d: { taux: number }) => `${Math.round(d.taux)} %`,
                  textAnchor: "start", dx: 4, fill: neutres.texte_secondaire,
                }),
                Plot.ruleX([0], { stroke: neutres.axe }),
              ],
            }}
          />
        </>
      )}

      {brouillon && (
        <>
          <h3>Commentaire</h3>
          <p className="aide">
            Rédigé par Claude à partir des indicateurs ci-dessus, modifiable ici.
            Il n'apparaît que sur votre document d'analyse, jamais sur les
            bilans des élèves.
          </p>

          <label className="champ">
            <span>Physionomie du devoir</span>
            <textarea
              rows={4}
              value={brouillon.synthese}
              onChange={(e) =>
                modifier((a) => ({ ...a, synthese: e.target.value }))
              }
            />
            <ApercuTypst texte={brouillon.synthese} />
          </label>

          {brouillon.groupes.map((groupe, index) => (
            <label className="champ" key={index}>
              <span>
                {NOMBRE.format(groupe.borne_basse)} à{" "}
                {NOMBRE.format(groupe.borne_haute)} — {groupe.intitule}
              </span>
              <textarea
                rows={3}
                value={groupe.commentaire}
                onChange={(e) =>
                  modifier((a) => ({
                    ...a,
                    groupes: a.groupes.map((g, i) =>
                      i === index ? { ...g, commentaire: e.target.value } : g,
                    ),
                  }))
                }
              />
              <ApercuTypst texte={groupe.commentaire} />
            </label>
          ))}

          {(["reussites", "fragilites", "pistes"] as const).map((cle) => (
            <div key={cle}>
              <span className="etiquette-liste">
                {cle === "reussites"
                  ? "Ce qui est acquis"
                  : cle === "fragilites"
                    ? "Ce qui a échoué"
                    : "Pistes pour la classe"}
              </span>
              {brouillon[cle].map((element, index) => (
                <div key={index}>
                  <textarea
                    rows={2}
                    className="element-liste"
                    value={element}
                    onChange={(e) =>
                      modifier((a) => ({
                        ...a,
                        [cle]: a[cle].map((x, i) => (i === index ? e.target.value : x)),
                      }))
                    }
                  />
                  <ApercuTypst texte={element} />
                </div>
              ))}
            </div>
          ))}

          <button type="button" onClick={enregistrer}>
            Enregistrer le commentaire
          </button>
        </>
      )}
    </section>
  );
}
